import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { INVOICE_TYPE_SUPPLIER, WccDto } from '@sr/dto';
import { Button, Divider, Grid, IconButton, MenuItem, Paper, Tooltip } from '@mui/material';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import Popper from '@mui/material/Popper';
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Grow from '@mui/material/Grow';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuList from '@mui/material/MenuList';
import Typography from '@mui/material/Typography';
import { fetchTaxInvoicePrintForm, fetchWccExportXML, fetchWccPrintForm } from 'features/wcc/wcc-print-form/api';
import { fetchWccUpdInvoicePrintForm } from 'features/wcc/wcc-print-form/api';
import { usePrintPopup } from 'utils/print-popup.hook';
import { formatDate } from 'utils/date-format-helpers';
import { useSnack } from 'shared/ui/snack';
import PriceSpan from 'shared/ui/price-span';
import { useInvoiceWcc } from 'features/invoice/invoice-wcc/lib';
import { WCC_STATUS_STYLE, WccStatusSpan } from './wcc-status-span';
import { WccSupplierInvoiceButton } from './wcc-button-invoice-supplier';
import { downloadFile } from 'utils/download-file';

type Props = {
  initialWcc: WccDto | null;
  invoiceId: number;
  invoiceType: number;
};

const WCC_NUMBER_VISIBLE_CHARS = 3;

// номера актов отличаются хвостом, поэтому обрезаем начало
const shortWccNumber = (wccNumber: string) => {
  const trimmed = wccNumber.trim();
  return trimmed.length > WCC_NUMBER_VISIBLE_CHARS ? `…${trimmed.slice(-WCC_NUMBER_VISIBLE_CHARS)}` : trimmed;
};

function WccTitle({ wcc }: { wcc: WccDto }) {
  return (
    <>
      <Divider textAlign="left">Общее</Divider>
      <Grid container direction="column" sx={{ pl: 2 }}>
        <Grid item>
          <Typography variant="caption">
            Сумма: <PriceSpan size="small" price={wcc.amount} />
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="caption">Дата: {formatDate(wcc.date)}</Typography>
        </Grid>
        <Grid item>
          <WccStatusSpan status={wcc.status} size="small" />
        </Grid>
      </Grid>
    </>
  );
}

function WccPrintButton({ wccId, withStamp = false }: { wccId: number; withStamp?: boolean }) {
  const { showError } = useSnack();

  const { openWindow } = usePrintPopup(
    useCallback(() => {
      return fetchWccPrintForm(wccId, withStamp).catch((e) => {
        showError(`Ошибка получения печатной формы: ${e}`);
        return '';
      });
    }, [wccId, showError])
  );
  return (
    <MenuItem dense onClick={openWindow}>
      Акт {withStamp ? '(с печатью)' : ''}
    </MenuItem>
  );
}

function TaxInvoicePrintButton({ wccId, withSignature }: { wccId: number; withSignature?: boolean }) {
  const { showError } = useSnack();

  const { openWindow } = usePrintPopup(
    useCallback(() => {
      return fetchTaxInvoicePrintForm(wccId, withSignature).catch((e) => {
        showError(`Ошибка получения печатной формы: ${e}`);
        return '';
      });
    }, [wccId, showError])
  );
  return (
    <MenuItem dense onClick={openWindow}>
      Счет фактура {withSignature ? '(с подписью)' : ''}
    </MenuItem>
  );
}

function WccUpdInvoicePrintButton({ wccId, withSignature }: { wccId: number; withSignature?: boolean }) {
  const { showError } = useSnack();

  const { openWindow } = usePrintPopup(
    useCallback(() => {
      return fetchWccUpdInvoicePrintForm(wccId, withSignature).catch((e) => {
        showError(`Ошибка получения печатной формы: ${e}`);
        return '';
      });
    }, [wccId, showError])
  );
  return (
    <MenuItem dense onClick={openWindow}>
      УПД {withSignature ? '(с подписью)' : ''}
    </MenuItem>
  );
}

export function WccExportXMLButton({ wccId }: { wccId: number }) {
  const { showError } = useSnack();
  
  const handleExport = async () => {
    try {
      const fileData = await fetchWccExportXML(wccId);
      downloadFile(`/api/files/private/${fileData.id}`, fileData.fileName);
    } catch (e) {
      showError(`Ошибка экспорта в XML: ${e}`);
    }
  };
  
  return (
    <MenuItem dense onClick={handleExport}>
      XML
    </MenuItem>
  );
}

export function WccButton({ initialWcc, invoiceId, invoiceType }: Props) {
  const { showSuccess, showError } = useSnack();
  const { invoiceWcc: wcc, createWcc, deleteWcc, isFetchingWcc, isPostingWcc } = useInvoiceWcc({ invoiceId, initialWcc });

  const handleCreateWcc = async () => {
    return createWcc()
      .then(() => showSuccess('Акт выполненных работ успешно создан'))
      .catch((e) => showError(`Ошибка создания акта выполненных работ: ${e}`));
  };

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  if (!wcc) {
    if (invoiceType === INVOICE_TYPE_SUPPLIER) return <WccSupplierInvoiceButton initialWcc={wcc} invoiceId={invoiceId} />;
    else
      return (
        <Tooltip title="Сформировать акт">
          <span>
            <IconButton size="small" onClick={handleCreateWcc} disabled={isPostingWcc}>
              <NoteAddOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      );
  }

  return (
    <>
      <ButtonGroup size="small" ref={anchorRef} sx={{ display: 'flex' }}>
        <Tooltip title={`Акт: ${wcc.wccNumber}`}>
          <Button
            size="small"
            color={WCC_STATUS_STYLE[wcc.status].buttonColor}
            variant={WCC_STATUS_STYLE[wcc.status].buttonVariant}
            sx={{ whiteSpace: 'nowrap', py: 0.5, px: 1, minWidth: 0 }}
            onClick={() => navigate(`/wcc/${wcc.id}`)}
          >
            Акт {shortWccNumber(wcc.wccNumber)}
          </Button>
        </Tooltip>
        <Button size="small" color={WCC_STATUS_STYLE[wcc.status].buttonColor} variant={WCC_STATUS_STYLE[wcc.status].buttonVariant} sx={{ width: 20 }} onClick={handleToggle}>
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{ 
          zIndex: 1400,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom'
            }}
          >
            <Paper
              elevation={24}
              sx={{
                maxHeight: '90vh',
                overflow: 'auto',
                maxWidth: 500,
                width: '100%',
                position: 'relative'
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  <WccTitle wcc={wcc} />
                  <Divider textAlign="left">Печать</Divider>
                  <WccPrintButton wccId={wcc?.id} />
                  <WccPrintButton wccId={wcc?.id} withStamp={true} />
                  <WccUpdInvoicePrintButton wccId={wcc?.id} />
                  <WccUpdInvoicePrintButton wccId={wcc?.id} withSignature={true} />
                  <TaxInvoicePrintButton wccId={wcc?.id} withSignature={true} />
                  <Divider textAlign="left">Экспорт</Divider>
                  <WccExportXMLButton wccId={wcc?.id} />
                  <Divider textAlign="left">Действия</Divider>
                  <MenuItem dense onClick={() => deleteWcc()}>
              Удалить
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
